import type { CartItem } from "@/hooks/useSales";

export type SalePet = { id: string; name: string };
export type CartPetSummary = { petId: string; name: string; count: number };

/** Pet que vai para a linha: o escolhido no item ou, se nenhum, o pet da venda (o da busca). */
export function resolveItemPetId(item: Pick<CartItem, "petId">, salePetId: string | null): string | null {
  return item.petId ?? salePetId ?? null;
}

/** A linha guarda só a exceção: escolher o próprio pet da venda volta a "seguir a venda". */
export function normalizeItemPetId(petId: string | null, salePetId: string | null): string | null {
  return petId && petId !== salePetId ? petId : null;
}

/** Escolher outro paciente na busca devolve todos os itens ao novo pet. */
export function clearCartItemPets<T extends Pick<CartItem, "petId">>(items: T[]): T[] {
  return items.some((item) => item.petId) ? items.map((item) => ({ ...item, petId: null })) : items;
}

/** Só soma na mesma linha um item que ainda segue o pet da venda; linha de outro pet fica separada. */
export function canMergeIntoLine(line: Pick<CartItem, "itemId" | "petId">, itemId: string): boolean {
  return line.itemId === itemId && !line.petId;
}

/**
 * "Pets nesta venda": unidades por pet (quantidade das linhas + cobranças de pacote incluídas), na ordem dos
 * pets do responsável; sem nada no carrinho, só o pet da busca.
 */
export function summarizeCartPets(
  items: Array<Pick<CartItem, "petId"> & { quantity?: number }>,
  salePetId: string | null,
  pets: SalePet[],
  packageCharges: Array<{ petId?: string | null; included: boolean }> = [],
): CartPetSummary[] {
  const counts = new Map<string, number>();
  const add = (petId: string | null, amount: number) => {
    if (petId) counts.set(petId, (counts.get(petId) ?? 0) + amount);
  };
  for (const item of items) add(resolveItemPetId(item, salePetId), item.quantity ?? 1);
  for (const charge of packageCharges) {
    if (charge.included) add(charge.petId ?? salePetId ?? null, 1);
  }
  const empty = items.length === 0 && !packageCharges.some((charge) => charge.included);
  return pets
    .filter((pet) => (counts.get(pet.id) ?? 0) > 0 || (empty && pet.id === salePetId))
    .map((pet) => ({ petId: pet.id, name: pet.name, count: counts.get(pet.id) ?? 0 }));
}
